import * as L from 'leaflet';
import { getWmsReadiness } from '../../../shared/map/wms-readiness';
import { FeatureCollection } from 'geojson';
import { combineWfsCollections } from '../oil-spill-prediction.model';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  merge,
  debounceTime,
  defer,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  VIEWPORT_BBOX_DEBOUNCE_MS,
  ViewportWfsService,
} from './viewport-wfs.service';
import {
  ViewportGeojsonHost,
  ViewportGeojsonSession,
} from './change-detection.types';

/**
 * Keeps every active Change Detection layer in sync with the current Leaflet viewport:
 * one debounced `moveend` stream feeds a `switchMap` so a newer viewport always cancels
 * the paging chain of the previous one.
 */

export class ViewportGeojsonController {
  private readonly sessions = new Map<string, ViewportGeojsonSession>();
  private readonly bounds$ = new Subject<L.LatLngBounds | null>();
  private readonly destroy$ = new Subject<void>();
  private readonly viewportStart$ = new Subject<void>();
  private readonly sessionEnded$ = new Subject<string>();
  private readonly refreshes = new Map<string, Subscription>();
  private subscription: Subscription | null = null;
  private listenerAttached = false;
  private lastViewportKey: string | null = null;
  // Internal pipeline loading (this class's own moveend/zoomend fetches) is kept
  // separate from `externalActiveLoads` (callers awaiting `loadInitialFeatures`/
  // their own fetch outside this pipeline) so neither can clear the host flag
  // out from under a load the other one started.
  private internalLoading = false;
  private externalActiveLoads = 0;

  constructor(
    private readonly host: ViewportGeojsonHost,
    private readonly wfsService: ViewportWfsService
  ) {}

  get activeSessionCount(): number {
    return this.sessions.size;
  }

  /** Marks one external (non-pipeline) fetch as in flight; pair with `endLoad()`. */
  beginLoad(): void {
    this.externalActiveLoads++;
    this.syncLoading();
  }

  /** Ends one external fetch started via `beginLoad()`. */
  endLoad(): void {
    this.externalActiveLoads = Math.max(0, this.externalActiveLoads - 1);
    this.syncLoading();
  }

  /** Hard-clears both internal and external loading state, e.g. on cancel/error. */
  resetLoading(): void {
    this.internalLoading = false;
    this.externalActiveLoads = 0;
    this.syncLoading();
  }

  /** Restart initial paging when the map moves, including while waiting for WMS. */
  loadInitialFeatures(session: ViewportGeojsonSession): Observable<FeatureCollection> {
    return defer(() => {
      this.setLoading(true);
      const events$ = new Observable<{ moving: boolean; initial?: boolean }>((subscriber) => {
        const map = this.host.map;
        const start = () => subscriber.next({ moving: true });
        const end = () => subscriber.next({ moving: false });
        map.on('movestart', start);
        map.on('zoomstart', start);
        map.on('moveend', end);
        map.on('zoomend', end);
        subscriber.next({ moving: false, initial: true });
        return () => {
          map.off('movestart', start);
          map.off('zoomstart', start);
          map.off('moveend', end);
          map.off('zoomend', end);
        };
      });
      return events$.pipe(
        switchMap(({ moving, initial }) => moving ? EMPTY : (initial ? of(0) : timer(VIEWPORT_BBOX_DEBOUNCE_MS)).pipe(
          switchMap(() => this.loadGatedFeatures(session, this.currentBounds()!, this.currentZoom()))
        )),
        take(1),
        takeUntil(session.renderMode === 'afterWmsLoad'
          ? getWmsReadiness(session.wmsLayer)?.pipe(filter((state) => state === 'removed')) ?? EMPTY
          : EMPTY),
        takeUntil(this.destroy$),
        finalize(() => this.setLoading(false))
      );
    });
  }

  startSession(session: ViewportGeojsonSession): void {
    this.refreshes.get(session.storageKey)?.unsubscribe();
    this.refreshes.delete(session.storageKey);
    this.sessionEnded$.next(session.storageKey);
    this.sessions.set(session.storageKey, session);
    // The caller already rendered this viewport, so don't immediately request it again.
    this.lastViewportKey = this.viewportKey(this.currentBounds());
    this.ensurePipeline();
    this.attachMapListener();
  }

  endSession(storageKey: string): void {
    this.refreshes.get(storageKey)?.unsubscribe();
    this.refreshes.delete(storageKey);
    if (!this.sessions.delete(storageKey)) return;
    this.sessionEnded$.next(storageKey);
    this.host.releaseViewportGeojsonSession?.(storageKey);
    if (this.sessions.size === 0) this.stop();
  }

  endSessionsFor(feedKey: string): void {
    Array.from(this.sessions.keys())
      .filter((key) => key === feedKey || key.startsWith(`${feedKey}::compare::`))
      .forEach((key) => this.endSession(key));
  }

  endAll(): void {
    Array.from(this.sessions.keys()).forEach(key => this.endSession(key));
    this.stop();
  }

  /** Refetch the current viewport through the same WMS gate used for pan and zoom. */
  refreshSession(storageKey: string): void {
    const session = this.sessions.get(storageKey);
    const bounds = this.currentBounds();
    if (!session || !bounds) return;
    this.refreshes.get(storageKey)?.unsubscribe();
    this.beginLoad();
    let request: Subscription | undefined;
    request = this.loadGatedFeatures(session, bounds, this.currentZoom()).pipe(
      takeUntil(this.viewportStart$),
      takeUntil(this.sessionEnded$.pipe(filter((key) => key === storageKey))),
      finalize(() => {
        this.endLoad();
        if (request && this.refreshes.get(storageKey) === request) this.refreshes.delete(storageKey);
      })
    ).subscribe({
      next: (features) => {
        if (this.sessions.get(storageKey) === session) {
          this.host.applyViewportGeojsonFeatures(storageKey, features);
        }
      },
      error: (error) => this.host.notifyViewportGeojsonLoadError(error),
    });
    if (!request.closed) this.refreshes.set(storageKey, request);
  }

  destroy(): void {
    this.endAll();
    this.destroy$.next();
    this.destroy$.complete();
    this.viewportStart$.complete();
    this.sessionEnded$.complete();
  }

  private stop(): void {
    this.detachMapListener();
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.lastViewportKey = null;
    this.setLoading(false);
  }

  private ensurePipeline(): void {
    if (this.subscription) return;

    this.subscription = this.bounds$
      .pipe(
        debounceTime(VIEWPORT_BBOX_DEBOUNCE_MS),
        filter((bounds): bounds is L.LatLngBounds => bounds !== null),
        map((bounds) => ({ bounds, key: this.viewportKey(bounds) })),
        filter(({ key }) => key !== this.lastViewportKey),
        tap(({ key }) => (this.lastViewportKey = key)),
        switchMap(({ bounds }) => this.loadSessions(bounds).pipe(takeUntil(this.viewportStart$))),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private loadSessions(bounds: L.LatLngBounds): Observable<unknown> {
    const sessions = Array.from(this.sessions.values());
    if (!sessions.length) return EMPTY;

    const zoom = this.currentZoom();
    this.setLoading(true);

    const pages$ = sessions.map((session) =>
      defer(() => this.sessions.get(session.storageKey) === session
        ? this.loadGatedFeatures(session, bounds, zoom)
        : EMPTY)
        .pipe(
          tap((featureCollection: FeatureCollection) =>
            this.host.applyViewportGeojsonFeatures(
              session.storageKey,
              featureCollection
            )
          ),
          catchError((error) => {
            this.lastViewportKey = null;
            this.host.notifyViewportGeojsonLoadError(error);
            return EMPTY;
          }),
          takeUntil(this.sessionEnded$.pipe(filter((key) => key === session.storageKey)))
        )
    );

    return merge(...pages$).pipe(finalize(() => this.setLoading(false)));
  }

  private loadGatedFeatures(
    session: ViewportGeojsonSession,
    bounds: L.LatLngBounds,
    zoom?: number
  ): Observable<FeatureCollection> {
    const features$ = defer(() => {
      const sources = session.sources?.length
        ? session.sources
        : [{ layerName: session.layerName, baseCql: session.baseCql }];
      const requests = sources.map((source) => this.wfsService.loadFeaturesForBounds(
        source.layerName, source.baseCql, bounds, { zoom }
      ));
      if (requests.length === 1) return requests[0];
      return forkJoin(requests).pipe(
        map((collections) => combineWfsCollections(collections) as FeatureCollection)
      );
    });
    if (session.renderMode !== 'afterWmsLoad') return features$;

    const readiness$ = getWmsReadiness(session.wmsLayer);
    if (!readiness$) {
      return throwError(() => new Error('WMS readiness is unavailable; WFS rendering is blocked'));
    }
    // Fetch WFS only after WMS succeeds; cancel an in-flight fetch if WMS reloads or fails.
    return readiness$.pipe(
      switchMap((state) => {
        if (state === 'failed') {
          throw new Error('WMS failed; WFS rendering is blocked');
        }
        return state === 'ready' ? features$ : EMPTY;
      }),
      take(1),
      takeUntil(readiness$.pipe(filter((state) => state === 'removed')))
    );
  }

  private attachMapListener(): void {
    const map = this.host.map;
    if (!map || this.listenerAttached) return;
    map.on('movestart', this.handleViewportStart);
    map.on('zoomstart', this.handleViewportStart);
    map.on('moveend', this.handleMoveEnd);
    map.on('zoomend', this.handleMoveEnd);
    map.on('dragend', this.handleMoveEnd);
    this.listenerAttached = true;
  }

  private detachMapListener(): void {
    const map = this.host.map;
    if (!map || !this.listenerAttached) return;
    map.off('movestart', this.handleViewportStart);
    map.off('zoomstart', this.handleViewportStart);
    map.off('moveend', this.handleMoveEnd);
    map.off('zoomend', this.handleMoveEnd);
    map.off('dragend', this.handleMoveEnd);
    this.listenerAttached = false;
  }

  private handleViewportStart = (): void => {
    this.viewportStart$.next();
    this.bounds$.next(null);
    this.lastViewportKey = null;
    this.clearRenderedSessions();
    this.setLoading(true);
  };

  private handleMoveEnd = (): void => {
    const bounds = this.currentBounds();
    if (bounds) this.bounds$.next(bounds);
  };

  private clearRenderedSessions(): void {
    Array.from(this.sessions.keys()).forEach((storageKey) =>
      this.host.clearViewportGeojsonFeatures(storageKey)
    );
  }

  private currentBounds(): L.LatLngBounds | null {
    try {
      return this.host.map?.getBounds() ?? null;
    } catch {
      return null;
    }
  }

  private currentZoom(): number | undefined {
    try {
      return this.host.map?.getZoom();
    } catch {
      return undefined;
    }
  }

  private setLoading(isLoading: boolean): void {
    this.internalLoading = isLoading;
    this.syncLoading();
  }

  private syncLoading(): void {
    this.host.viewportGeojsonLoading = this.internalLoading || this.externalActiveLoads > 0;
  }

  private viewportKey(bounds: L.LatLngBounds | null): string | null {
    if (!bounds) return null;
    const layers = Array.from(this.sessions.values())
      .map((session) => `${session.storageKey}|${session.layerName}`)
      .sort()
      .join(',');
    return [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
      this.currentZoom(),
      layers,
    ].join(':');
  }
}
